import {FC, useEffect, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router'
import {Link, NavLink} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {clearUser, useUser} from 'entities/user'
import {FreshChatOpenSupport} from 'features/freshChat'
import {upgradeHandler} from 'features/payment/lib'
import {mediaVariants, names} from 'shared/lib'
import {text} from 'shared/mirrors/text'
import {Avatar} from 'shared/ui/avatar'
import {Button} from 'shared/ui/buttons'
import {HorizontalDivider} from 'shared/ui/dividers/horizontalDivider'
import {Credits, Cross, HeadphoneOutline, Logout, Profile, SettingsOutline} from 'shared/ui/icons'
import styles from './styles.module.scss'

export const SidebarMobile: FC<{isActive: boolean; onClose: VoidFunction}> = ({
  isActive,
  onClose
}) => {
  const {profile, paymentsCount, support: {enabled}} = useUser()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const popupRef = useRef<HTMLDivElement>(null)
  const mobileSidebarRef = useRef<HTMLDivElement>(null)
  const [startX, setStartX] = useState<number>(0)
  const [currentX, setCurrentX] = useState<number>(0)

  const handleClickOffer = () => {
    onClose()
    upgradeHandler({dispatch, paymentsCount: paymentsCount})
  }

  const signOut = () => {
    dispatch(clearUser())
    location.reload()
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileSidebarRef.current &&
        !mobileSidebarRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (isActive) {
        setStartX(event.touches[0].clientX)
        setCurrentX(0)
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (isActive) {
        const touchX = event.touches[0].clientX
        const diffX = touchX - startX
        setCurrentX(diffX)
      }
    }

    const handleTouchEnd = () => {
      if (isActive && currentX < -50) {
        onClose()
      }
      setCurrentX(0)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleTouchStart)
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', handleTouchEnd)

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isActive, startX, currentX])

  return (
    <CSSTransition
      classNames={{
        enterActive: styles.fade_enter_active,
        enterDone: styles.fade_enter,
        exitActive: styles.fade_exit_active,
        exitDone: styles.fade_exit
      }}
      in={isActive}
      timeout={Number(styles.animateTimeMs)}
      unmountOnExit
      nodeRef={popupRef}
    >
      <div
        className={styles.popup}
        ref={popupRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.mobileSidebar} ref={mobileSidebarRef}>
          <div className={styles.mobileSidebarHeader}>
            <div
              onClick={onClose}
              className={styles.closeIcon}
            >
              <Cross />
            </div>
            <p className={styles.mobileSidebarTitle}>Account</p>
          </div>
          <div className={styles.topContainer}>
            <Link to='/myProfile' className={styles.user}>
              <Avatar
                sx={{width: 52, height: 52}}
                alt={profile.name}
                src={
                  profile.avatarUrl
                    ? profile.avatarUrl + mediaVariants.square64
                    : ''
                }
              />
              <div className={styles.userText}>
                <span className={styles.userName}>{profile.name}</span>
                <span className={styles.userEmail}>{profile.email}</span>
              </div>
            </Link>
            <Button
              className={styles.upgradeBtn}
              styleType='primary'
              onClick={handleClickOffer}
            >
              {paymentsCount > 0 ? 'Refill credits' : 'Upgrade account'}
            </Button>
          </div>
          <div className={styles.menuContainer}>
            <NavLink to='/myProfile' className={styles.menuItem}>
              <div className={styles.menuItem__icon}>
                <Profile />
              </div>
              <span>My Profile</span>
            </NavLink>
            <NavLink to='/credits' className={styles.menuItem}>
              <div className={styles.menuItem__icon}><Credits /></div>
              <span>Credits</span>
            </NavLink>
            {/*<div className={styles.menuItem}>
              <div className={styles.menuItem__icon}><DarkMode /></div>
              <span>Dark mode</span>
            </div>*/}
            <div
              className={styles.menuItem}
              onClick={() => {
                onClose()
                navigate('/settings')
              }}
            >
              <div className={styles.menuItem__icon}><SettingsOutline /></div>
              <span>Settings</span>
            </div>
            {enabled ?
              <div
                className={styles.menuItem}
                onClick={() => {
                  onClose()
                  FreshChatOpenSupport()
                }}
              >
                <div className={styles.menuItem__icon}><HeadphoneOutline /></div>
                <span>Support</span>
              </div>
              : null}
            <HorizontalDivider className={styles.divider} />
            <div className={names(styles.menuItem)} onClick={signOut}>
              <div className={`${styles.menuItem__icon} ${styles.menuItem__logout}`}>
                <Logout />
              </div>
              <span className={styles.signOut}>Sign out</span>
            </div>
            <HorizontalDivider className={styles.divider} />
          </div>
          <div className={styles.footer}>
            <Link to='/privacy-policy' className={styles.footerLink}>
              Privacy
            </Link>
            <Link to='/terms-of-use' className={styles.footerLink}>
              Terms
            </Link>
            <Link to='/dating-safety' className={styles.footerLink}>
              Dating Safety
            </Link>
            <Link to='/community-rules' className={styles.footerLink}>
              Rules
            </Link>
          </div>
          <p className={styles.copy}>
            {text.main.siteName} © {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </CSSTransition>
  )
}